// src/components/Temperature.jsx
import React, { useEffect, useState } from 'react';
import '../styles.css';
import { useNavigate } from 'react-router-dom';

const Temperature = () => {
  const [temperature, setTemperature] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState('Disconnected');
  const [lastUpdate, setLastUpdate] = useState(null);
  const navigate = useNavigate();

  // ThingSpeak Configuration
  const CHANNEL_ID = '3018449';
  const READ_API_KEY = 'WPIXJNON8E0WC5U0';

  const getThingSpeakUrl = () => {
    const baseUrl = `https://api.thingspeak.com/channels/${CHANNEL_ID}/feeds/last.json`;
    return READ_API_KEY ? `${baseUrl}?api_key=${READ_API_KEY}` : baseUrl;
  };

  useEffect(() => {
    const fetchTemperatureData = async () => {
      try {
        setConnectionStatus('Fetching...');
        const response = await fetch(getThingSpeakUrl());
        if (response.ok) {
          const data = await response.json();

          if (!data.created_at) {
            setConnectionStatus('Device Offline');
            setTemperature(null);
            setLastUpdate(null);
            return;
          }

          const lastUpdateTime = new Date(data.created_at);
          const now = new Date();
          const diffMinutes = (now - lastUpdateTime) / 1000 / 60;

          if (diffMinutes > 5) {
            setConnectionStatus('Device Offline');
            setTemperature(null);
            setLastUpdate(null);
            return;
          }

          const tempValue = parseFloat(data.field1);
          if (!isNaN(tempValue)) {
            setTemperature(tempValue);
            setConnectionStatus('Connected');
            setLastUpdate(lastUpdateTime.toLocaleTimeString());
          } else {
            console.warn('Received invalid temperature:', data.field1);
            setTemperature(null);
          }
        } else {
          setConnectionStatus('Error Fetching');
        }
      } catch (error) {
        console.error('Error fetching ThingSpeak data:', error);
        setConnectionStatus('Error');
      }
    };

    fetchTemperatureData();
    const interval = setInterval(fetchTemperatureData, 30000);
    return () => clearInterval(interval);
  }, []);

  const getStatusIndicator = () => {
    if (typeof temperature !== 'number' || temperature === 0) return null;
    const inRange = temperature >= 18 && temperature <= 27;
    return (
      <div className={`status-indicator ${inRange ? 'good' : 'bad'}`}>
        <span className="material-icons emoji">
          {inRange ? 'sentiment_satisfied_alt' : 'sentiment_dissatisfied'}
        </span>
        <span className="label">{inRange ? 'Good' : 'Bad'}</span>
      </div>
    );
  };

  const displayTemp =
    typeof temperature === 'number' ? `${temperature.toFixed(2)}°C` : 'Loading...';

  return (
    <div className="main-content">
      <div className="temp-container">
        <h2>Temperature 🌡</h2>

        <div
          className="connection-status"
          style={{
            color: connectionStatus === 'Connected' ? '#22cf27ff' : '#f44336',
            fontSize: '14px',
            marginBottom: '10px',
          }}
        >
          <strong>ThingSpeak Status:</strong> {connectionStatus}
          {lastUpdate && (
            <span style={{ marginLeft: '15px' }}>Last Update: {lastUpdate}</span>
          )}
        </div>

        {/* Conditional rendering */}
        {connectionStatus === 'Device Offline' ? (
          <div
            style={{
              color: '#f44336',
              fontWeight: 'bold',
              textAlign: 'center',
              padding: '20px',
              fontSize: '16px',
            }}
          >
            ⚠️ Device Offline or No Recent Data
          </div>
        ) : temperature === 0 ? (
          <div
            style={{
              color: '#f44336',
              fontWeight: 'bold',
              textAlign: 'center',
              padding: '20px',
              fontSize: '16px',
            }}
          >
            ⚠️ Error in the sensor or sensor disconnected
          </div>
        ) : (
          <div className="status-card layout-horizontal">
            <span className="status-icon temp-icon">🌡</span>
            <div className="status-info">
              <p style={{fontSize:'18px'}}>Temperature: {displayTemp}</p>
            </div>
            {getStatusIndicator()}
          </div>
        )}

        <h3>Temperature Ranges</h3>
        <div className="ranges">
          <div className="range-item">
            <span className="range-icon">✅</span>
            <p>Recommended: 18°C to 27°C</p>
          </div>
          <div className="range-item">
            <span className="range-icon">✅</span>
            <p>Ideal: Around 22°C to 24°C</p>
          </div>
        </div>
        <button 
              className='ViewLogsButton' 
              onClick={() => navigate('/dashboard/data-history')}
        >
          View Logs
        </button>

      </div>
    </div>
  );
};

export default Temperature;
